// Relevés d'heures — lecture et écriture de `releves_heures`, des alias de
// prénom (`employes.metadata.aliasHeures`) et de la règle de pause
// (`organizations.parametres.relevesHeures`).
//
// Le raisonnement — ce qui est dû, ce qui manque, comment lire un message
// WhatsApp — vit dans @imp/shared/releve-heures et
// @imp/shared/releve-heures-whatsapp, purs et testés contre les vrais messages
// du groupe. Ici on ne fait que parler à la base.
//
// La table peut ne pas exister encore : la migration se déploie séparément du
// site. Une lecture qui échoue là-dessus rend une liste vide plutôt qu'une
// page en erreur — c'est la règle de la maison.

import { tableAbsente } from '@imp/shared/erreurs-supabase';
import { REGLE_PAUSE_DEFAUT, type ReglePause, type ReleveHeures } from '@imp/shared/releve-heures';
import { type Salarie } from '@imp/shared/releve-heures-whatsapp';

import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

export interface EmployeHeures {
  id: string;
  nom: string;
  prenom: string;
  /** Les autres orthographes du prénom : « Arnoux » pour Arnaud. */
  aliasHeures: string[];
}

interface ReleveRow {
  id: string;
  jour: string;
  employe_id: string | null;
  nom: string;
  equipe: string | null;
  chantier_id: string | null;
  chantier_libelle: string | null;
  debut_minutes: number;
  fin_minutes: number;
  pause_minutes: number | null;
  note: string | null;
  source: string;
  source_texte: string | null;
}

const COLONNES =
  'id,jour,employe_id,nom,equipe,chantier_id,chantier_libelle,debut_minutes,fin_minutes,pause_minutes,note,source,source_texte';

const TABLE = 'releves_heures';

function versReleve(r: ReleveRow): ReleveHeures {
  return {
    id: r.id,
    jour: r.jour.slice(0, 10),
    employeId: r.employe_id,
    nom: r.nom,
    equipe: r.equipe,
    chantierId: r.chantier_id,
    chantierLibelle: r.chantier_libelle,
    debut: r.debut_minutes,
    fin: r.fin_minutes,
    pauseDeclaree: r.pause_minutes,
    note: r.note,
  };
}

export interface ReleveDetail extends ReleveHeures {
  source: string;
  sourceTexte: string | null;
}

export async function listReleves(du: string, au: string): Promise<ReleveDetail[]> {
  const { data, error } = await supabase
    .from('releves_heures')
    .select(COLONNES)
    .is('deleted_at', null)
    .gte('jour', du)
    .lte('jour', au)
    .order('jour', { ascending: false });
  if (error) {
    if (tableAbsente(error.message, TABLE)) return [];
    throw new Error(error.message);
  }
  return ((data as ReleveRow[] | null) ?? []).map((r) => ({
    ...versReleve(r),
    source: r.source,
    sourceTexte: r.source_texte,
  }));
}

export async function listEmployesHeures(): Promise<EmployeHeures[]> {
  const { data, error } = await supabase
    .from('employes')
    .select('id,nom,prenom,metadata')
    .eq('actif', true)
    .order('prenom');
  if (error) throw new Error(error.message);
  const rows =
    (data as { id: string; nom: string; prenom: string; metadata: unknown }[] | null) ?? [];
  return rows.map((e) => {
    const meta = e.metadata as { aliasHeures?: unknown } | null;
    const alias = Array.isArray(meta?.aliasHeures) ? meta.aliasHeures : [];
    return {
      id: e.id,
      nom: e.nom,
      prenom: e.prenom,
      aliasHeures: alias.filter((a): a is string => typeof a === 'string'),
    };
  });
}

/** Les salariés sous la forme attendue par le lecteur de messages. */
export function versSalaries(employes: readonly EmployeHeures[]): Salarie[] {
  return employes.map((e) => ({
    employeId: e.id,
    prenom: e.prenom,
    alias: e.aliasHeures,
  }));
}

export async function setAliasEmploye(employeId: string, alias: readonly string[]): Promise<void> {
  const { data, error } = await supabase
    .from('employes')
    .select('metadata')
    .eq('id', employeId)
    .single();
  if (error) throw new Error(error.message);
  // On relit et on fusionne : `metadata` porte d'autres champs qu'il ne faut
  // pas écraser au passage.
  const meta = ((data as { metadata: unknown } | null)?.metadata ?? {}) as Record<string, unknown>;
  const propres = [...new Set(alias.map((a) => a.trim()).filter((a) => a !== ''))];
  const { error: upErr } = await supabase
    .from('employes')
    .update({ metadata: { ...meta, aliasHeures: propres } })
    .eq('id', employeId);
  if (upErr) throw new Error(upErr.message);
}

export interface SaisieReleve {
  jour: string;
  employeId: string | null;
  nom: string;
  equipe: string | null;
  chantierId: string | null;
  chantierLibelle: string | null;
  debut: number;
  fin: number;
  pauseDeclaree: number | null;
  note: string | null;
  source: 'saisie' | 'whatsapp';
  sourceTexte: string | null;
}

function versRow(s: SaisieReleve): Record<string, unknown> {
  return {
    organization_id: ORG_ID,
    jour: s.jour,
    employe_id: s.employeId,
    nom: s.nom,
    equipe: s.equipe,
    chantier_id: s.chantierId,
    chantier_libelle: s.chantierLibelle,
    debut_minutes: s.debut,
    fin_minutes: s.fin,
    pause_minutes: s.pauseDeclaree,
    note: s.note,
    source: s.source,
    source_texte: s.sourceTexte,
  };
}

export async function creerReleve(s: SaisieReleve): Promise<void> {
  const { error } = await supabase.from('releves_heures').insert(versRow(s));
  if (error) throw new Error(error.message);
}

/** Enregistre un import en une fois. Rend le nombre de lignes écrites. */
export async function creerReleves(lignes: readonly SaisieReleve[]): Promise<number> {
  if (lignes.length === 0) return 0;
  const { error } = await supabase.from('releves_heures').insert(lignes.map(versRow));
  if (error) throw new Error(error.message);
  return lignes.length;
}

export async function modifierReleve(id: string, s: Partial<SaisieReleve>): Promise<void> {
  const champs: Record<string, unknown> = {};
  if (s.jour !== undefined) champs.jour = s.jour;
  if (s.employeId !== undefined) champs.employe_id = s.employeId;
  if (s.nom !== undefined) champs.nom = s.nom;
  if (s.equipe !== undefined) champs.equipe = s.equipe;
  if (s.chantierId !== undefined) champs.chantier_id = s.chantierId;
  if (s.chantierLibelle !== undefined) champs.chantier_libelle = s.chantierLibelle;
  if (s.debut !== undefined) champs.debut_minutes = s.debut;
  if (s.fin !== undefined) champs.fin_minutes = s.fin;
  if (s.pauseDeclaree !== undefined) champs.pause_minutes = s.pauseDeclaree;
  if (s.note !== undefined) champs.note = s.note;
  if (Object.keys(champs).length === 0) return;
  const { error } = await supabase.from('releves_heures').update(champs).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Suppression douce : la ligne reste, pour pouvoir expliquer un écart de paie. */
export async function supprimerReleve(id: string): Promise<void> {
  const { error } = await supabase
    .from('releves_heures')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ----- Règle de pause -------------------------------------------------------

interface ParametresOrg {
  relevesHeures?: Partial<ReglePause>;
  [cle: string]: unknown;
}

async function lireParametres(): Promise<ParametresOrg> {
  const { data, error } = await supabase
    .from('organizations')
    .select('parametres')
    .eq('id', ORG_ID)
    .single();
  if (error) throw new Error(error.message);
  const p = (data as { parametres: unknown } | null)?.parametres;
  return p && typeof p === 'object' ? (p as ParametresOrg) : {};
}

export async function getReglePause(): Promise<ReglePause> {
  const p = await lireParametres();
  return { ...REGLE_PAUSE_DEFAUT, ...(p.relevesHeures ?? {}) };
}

export async function setReglePause(regle: ReglePause): Promise<void> {
  // Les autres réglages de l'organisation vivent dans le même objet : on relit,
  // on fusionne, on réécrit — jamais on n'écrase.
  const p = await lireParametres();
  const { error } = await supabase
    .from('organizations')
    .update({ parametres: { ...p, relevesHeures: regle } })
    .eq('id', ORG_ID);
  if (error) throw new Error(error.message);
}
